<template>
    <div>
        <Nav></Nav>
        <b-container fluid="xl">
            <b-row>
                <b-col>
                    <h1>Copiador de Menús Uber</h1>
                    <p class="text-muted">Selecciona un menú origen y los menús destino para realizar la copia</p>
                </b-col>
            </b-row>

            <!-- Selección de Menú Origen -->
            <b-row class="mt-4">
                <b-col>
                    <b-card>
                        <template #header>
                            <h4 class="mb-0">
                                <i class="fas fa-file-export"></i> Menú Origen
                            </h4>
                        </template>

                        <b-form-group label="Selecciona el menú que se copiará:" label-for="origin-menu">
                            <b-form-select id="origin-menu" v-model="selectedOriginMenu" :options="menuOptions"
                                placeholder="Seleccionar menú origen..." @change="onOriginMenuChange">
                                <template #first>
                                    <b-form-select-option :value="null" disabled>
                                        -- Selecciona un menú origen --
                                    </b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-form-group>

                        <div v-if="selectedOriginMenu" class="mt-3">
                            <b-alert show variant="info">
                                <strong>Menú seleccionado:</strong> {{ getMenuName(selectedOriginMenu) }}
                            </b-alert>
                        </div>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Selección de Menús Destino -->
            <b-row class="mt-4" v-if="selectedOriginMenu">
                <b-col>
                    <b-card>
                        <template #header>
                            <h4 class="mb-0">
                                <i class="fas fa-file-import"></i> Menús Destino
                            </h4>
                        </template>

                        <b-form-group label="Selecciona los menús donde se copiará el contenido:">
                            <div class="destination-menus-container">
                                <div v-for="menu in availableDestinationMenus" :key="menu.value" class="mb-2 ml-3">
                                    <b-form-checkbox v-model="selectedDestinationMenus" :value="menu.value"
                                        class="destination-menu-checkbox">
                                        <strong>{{ menu.text }}</strong>
                                    </b-form-checkbox>
                                </div>
                            </div>
                        </b-form-group>

                        <div v-if="selectedDestinationMenus.length > 0" class="mt-3">
                            <b-alert show variant="warning">
                                <i class="fas fa-exclamation-triangle"></i>
                                <strong>{{ selectedDestinationMenus.length }}</strong> menú(s) seleccionado(s) para
                                copia.
                                <br><small>El contenido actual de estos menús será reemplazado.</small>
                            </b-alert>
                        </div>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Botón de Acción -->
            <b-row class="mt-4" v-if="selectedOriginMenu && selectedDestinationMenus.length > 0">
                <b-col>
                    <b-card>
                        <b-button variant="primary" size="lg" block @click="showConfirmModal" :disabled="copying">
                            <i class="fas fa-copy"></i>
                            Copiar Menú a {{ selectedDestinationMenus.length }} Destino(s)
                        </b-button>
                    </b-card>
                </b-col>
            </b-row>

            <!-- Progreso de Copia -->
            <b-row class="mt-4" v-if="copying">
                <b-col>
                    <b-card>
                        <template #header>
                            <h4 class="mb-0">
                                <i class="fas fa-spinner fa-spin"></i> Progreso de Copia
                            </h4>
                        </template>

                        <b-progress :value="copyProgress" :max="selectedDestinationMenus.length" show-progress animated
                            variant="primary" class="mb-3"></b-progress>

                        <div class="copy-status">
                            <p><strong>{{ copyProgress }}</strong> de <strong>{{ selectedDestinationMenus.length
                                    }}</strong> menús
                                copiados</p>
                            <div v-if="currentCopyingMenu" class="text-muted">
                                <small>Copiando a: {{ getMenuName(currentCopyingMenu) }}...</small>
                            </div>
                        </div>

                        <!-- Log de operaciones -->
                        <div v-if="copyLog.length > 0" class="mt-3">
                            <h6>Registro de operaciones:</h6>
                            <div class="copy-log">
                                <div v-for="(log, index) in copyLog" :key="index" :class="['log-entry', log.type]">
                                    <i :class="log.icon"></i>
                                    {{ log.message }}
                                </div>
                            </div>
                        </div>
                    </b-card>
                </b-col>
            </b-row>

        </b-container>

        <!-- Modal de Confirmación -->
        <b-modal id="confirm-copy-modal" v-model="showModal" title="Confirmar Copia de Menú" @ok="startCopy"
            @cancel="showModal = false" ok-title="Sí, Copiar" cancel-title="Cancelar" ok-variant="danger">
            <div class="text-center">
                <i class="fas fa-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
                <h5 class="mt-3">⚠️ Advertencia Importante</h5>
                <p class="text-danger">
                    <strong>El contenido actual de los menús destino será eliminado completamente antes de
                        copiar.</strong>
                </p>
                <hr>
                <div class="text-left">
                    <p><strong>Menú Origen:</strong> {{ getMenuName(selectedOriginMenu) }}</p>
                    <p><strong>Menús Destino ({{ selectedDestinationMenus.length }}):</strong></p>
                    <ul class="pl-4">
                        <li v-for="menuId in selectedDestinationMenus" :key="menuId">
                            {{ getMenuName(menuId) }}
                        </li>
                    </ul>
                </div>
                <p class="mt-3">
                    <strong>¿Estás seguro de que deseas continuar?</strong>
                </p>
            </div>
        </b-modal>

    </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import axios from "axios";

export default {
    name: "UberMenuCopy",
    components: { Nav },
    data() {
        return {
            // Datos de menús
            allMenus: [],
            selectedOriginMenu: null,
            selectedDestinationMenus: [],

            // Estados de UI
            loading: false,
            copying: false,
            showModal: false,

            // Progreso de copia
            copyProgress: 0,
            currentCopyingMenu: null,
            copyLog: [],

            // Control de errores
            hasErrors: false
        };
    },
    computed: {
        menuOptions() {
            return this.allMenus.map(menu => ({
                value: menu.uuid,
                text: menu.name
            }));
        },

        availableDestinationMenus() {
            return this.allMenus
                .filter(menu => menu.uuid !== this.selectedOriginMenu)
                .map(menu => ({
                    value: menu.uuid,
                    text: menu.name
                }));
        }
    },
    mounted() {
        this.loadMenus();
    },
    methods: {
        async loadMenus() {
            this.loading = true;
            try {
                const response = await axios.get('/uber/mastermenus');
                this.allMenus = response.data;
                this.makeToast('Éxito', 'Menús cargados correctamente', 'success');
            } catch (error) {
                this.makeToast('Error', 'Error al cargar los menús: ' + (error.response?.data || error.message), 'danger');
            } finally {
                this.loading = false;
            }
        },

        onOriginMenuChange() {
            // Limpiar selecciones de destino si cambia el origen
            this.selectedDestinationMenus = [];
            this.copyLog = [];
            this.copyProgress = 0;
        },

        getMenuName(menuUuid) {
            const menu = this.allMenus.find(m => m.uuid === menuUuid);
            return menu ? menu.name : 'Menú desconocido';
        },

        showConfirmModal() {
            this.showModal = true;
        },

        async startCopy() {
            this.copying = true;
            this.copyProgress = 0;
            this.copyLog = [];
            this.hasErrors = false;

            this.addLog('Iniciando proceso de copia...', 'info');

            for (let i = 0; i < this.selectedDestinationMenus.length; i++) {
                const destinationMenuId = this.selectedDestinationMenus[i];
                this.currentCopyingMenu = destinationMenuId;

                try {
                    this.addLog(`Copiando a ${this.getMenuName(destinationMenuId)}...`, 'info');
                    
                    await this.copyMenuToDestination(this.selectedOriginMenu, destinationMenuId);

                    this.copyProgress++;
                    this.addLog(`✅ Copia exitosa a ${this.getMenuName(destinationMenuId)}`, 'success');

                    // Mostrar toast temporal de progreso
                    this.makeToast(
                        'Progreso',
                        `Menú copiado a ${this.getMenuName(destinationMenuId)} (${this.copyProgress}/${this.selectedDestinationMenus.length})`,
                        'info',
                        3000
                    );

                } catch (error) {
                    this.hasErrors = true;
                    this.addLog(`❌ Error al copiar a ${this.getMenuName(destinationMenuId)}: ${error.message}`, 'error');
                    this.makeToast('Error', `Error al copiar a ${this.getMenuName(destinationMenuId)}`, 'danger');
                }
            }

            this.currentCopyingMenu = null;
            this.copying = false;

            // Mostrar resultado final
            if (this.hasErrors) {
                this.makeToast(
                    'Copia Completada con Errores',
                    `Se completó la copia con algunos errores. Revisa el registro para más detalles.`,
                    'warning',
                    8000
                );
            } else {
                this.makeToast(
                    'Copia Completada',
                    `Se copiaron exitosamente ${this.copyProgress} menús desde ${this.getMenuName(this.selectedOriginMenu)}`,
                    'success',
                    6000
                );
            }

            this.addLog(`Proceso finalizado. Total copiados: ${this.copyProgress}/${this.selectedDestinationMenus.length}`, 'info');
        },

        async copyMenuToDestination(originMenuUuid, destinationMenuUuid) {
            const response = await axios.post(`/uber/mastermenu/${originMenuUuid}/copyTo/${destinationMenuUuid}`);
            return response.data;
        },

        addLog(message, type = 'info') {
            const iconMap = {
                info: 'fas fa-info-circle text-info',
                success: 'fas fa-check-circle text-success',
                error: 'fas fa-times-circle text-danger',
                warning: 'fas fa-exclamation-triangle text-warning'
            };

            this.copyLog.push({
                message,
                type,
                icon: iconMap[type] || iconMap.info,
                timestamp: new Date().toLocaleTimeString()
            });
        },

        makeToast(title, content, variant, autoHideDelay = 5000) {
            this.$bvToast.toast(content, {
                title,
                variant,
                autoHideDelay,
                appendToast: true,
                toaster: 'b-toaster-top-right'
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.destination-menus-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #f8f9fa;
}

.destination-menu-checkbox {
    padding: 0.5rem;
    border-radius: 0.25rem;
    transition: background-color 0.2s;

    &:hover {
        background-color: #e9ecef;
    }
}

.copy-log {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 0.75rem;
    background-color: #f8f9fa;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
}

.log-entry {
    margin-bottom: 0.25rem;
    padding: 0.25rem;
    border-radius: 0.125rem;

    &.success {
        background-color: #d4edda;
        border-left: 3px solid #28a745;
    }

    &.error {
        background-color: #f8d7da;
        border-left: 3px solid #dc3545;
    }

    &.warning {
        background-color: #fff3cd;
        border-left: 3px solid #ffc107;
    }

    &.info {
        background-color: #d1ecf1;
        border-left: 3px solid #17a2b8;
    }
}

.copy-status {
    text-align: center;
}

.card-header h4 {
    color: #495057;
}

// Animaciones
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

// Responsive adjustments
@media (max-width: 768px) {
    .destination-menus-container {
        max-height: 200px;
    }

    .copy-log {
        max-height: 150px;
        font-size: 0.75rem;
    }
}
</style>